import { Display } from "rot-js"
import { Color } from "../defines/colors"
import { PlayerAction } from "../defines/enums"
import { EquippableItem, Offset } from "../defines/types"
import { EngineDefaults, EquipmentDefaults } from "../defines/values"
import { BLACK_TILE, BORDER_TILE, TapestryTile } from "../map/tile"
import { toDisplayString } from "../utils/color"
import { statsToDisplayString } from "../utils/misc"
import { Popup } from "./popup"

export class Cabinet extends Popup {
  private _tiles: TapestryTile[][] = []
  private _columnWidth: number = 0

  private _description: string

  private _newStats: EquippableItem = EquipmentDefaults.NONE
  private _startingStats: EquippableItem = EquipmentDefaults.NONE
  private _playerItems: EquippableItem[][] = []
  private _playerItemsCount: number = 0
  private _selectedItems: number[] = []
  private _currentColumn: number = 0
  private _currentRow: number = 0

  private _displayFunc: (e: EquippableItem[]) => EquippableItem[][]

  protected _errorMsg: string = ""

  constructor(
    offsets: Offset,
    name: string,
    description: string,
    displayFunc: (e: EquippableItem[]) => EquippableItem[][]
  ) {
    super(
      EngineDefaults.TROPHY_CASE_WIDTH,
      EngineDefaults.TROPHY_CASE_HEIGHT,
      offsets,
      name
    )
    this._displayFunc = displayFunc

    this._description = description

    this.initTiles()
  }

  private getSelectedEquipment(): EquippableItem[] {
    let equipped: EquippableItem[] = []

    this._selectedItems.forEach((item, idx) => {
      const e = this._playerItems[idx][item]
      equipped.push(e)
    })

    return equipped
  }

  prepareToRender(): void {
    const player = window.engine.getPlayer()
    this._playerItems = this._displayFunc(player.equipment)
    this._columnWidth = (this.width - 4) / this._playerItems.length

    this._selectedItems = new Array(this._playerItems.length)
    for (let i = 0; i < this._selectedItems.length; ++i) {
      this._selectedItems[i] = 0
    }

    // Mark currently equipped items
    this._playerItems.forEach((group, column) => {
      this._playerItemsCount += group.length
      group.forEach((item, row) => {
        if (item.equipped) {
          this._selectedItems[column] = row
        }
      })
    })

    this._startingStats = player.getEquipmentStats()
    this._newStats = { ...this._startingStats }
  }

  postRender(): void {}

  render(display: Display): void {
    for (let y = 0; y < this._tiles.length; ++y) {
      const row = this._tiles[y]
      for (let x = 0; x < row.length; ++x) {
        const tile = row[x]
        display.draw(
          x + this.offsets.xOffset,
          y + this.offsets.yOffset,
          tile.symbol,
          toDisplayString(tile.fg),
          toDisplayString(tile.bg)
        )
      }
    }

    if (this._playerItemsCount == 0) {
      this._errorMsg = "No equipment available to select."
      this._showConfirmDialog = true
    }

    if (this._showConfirmDialog) {
      const message: string = this._errorMsg + " ESC to cancel"
      const xStart = this.width / 2 - message.length / 2
      const yStart = this.height / 2 - 2
      const blank: string = ".".padStart(message.length, ".")
      display.drawText(xStart, yStart, `%c{#fff}%b{#000}${message}`)
    } else {
      let colors: string = "%c{#fff}%b{#000}"
      const title: string = this.name
      let printRow: number = 1
      let xStart: number =
        this.width / 2 - title.length / 2 + this.offsets.xOffset

      display.drawText(xStart, printRow, colors + title)

      let output: string = this._description

      printRow += 2
      xStart = this.width / 2 - output.length / 2
      display.drawText(xStart, printRow, colors + output)

      printRow += 1
      xStart = this.offsets.xOffset + 1
      display.drawText(
        xStart,
        printRow,
        colors + "-".padEnd(this.width - 2, "-") // 2 accounts for walls
      )

      printRow += 1

      output = statsToDisplayString(this._startingStats, this._newStats)

      let padding = (this.width - 2 - output.length) / 2
      const padStrFront = ".".padStart(padding, ".")
      if ((this.width - output.length) % 2 == 1) {
        padding += 1
      }
      const padStrBack = ".".padStart(padding, ".")

      xStart += 1
      display.drawText(
        xStart,
        printRow,
        `%c{${Color.STATS_BG}}%b{${Color.STATS_BG}}` + padStrFront
      )
      xStart = padStrFront.length + 1
      display.drawText(
        xStart,
        printRow,
        `%c{#000}%b{${Color.STATS_BG}}` + output
      )
      display.drawText(
        xStart + output.length,
        printRow,
        `%c{${Color.STATS_BG}}%b{${Color.STATS_BG}}` + padStrBack
      )

      printRow += 1
      xStart = this.offsets.xOffset + 2

      const rowTop = printRow
      this._playerItems.forEach((group, column) => {
        if (column == this._currentColumn) {
          const marker = ".".padStart(this._columnWidth / 2, ".")
          display.drawText(xStart, printRow, "%c{#0ff}%b{#0ff}" + marker)
        }

        group.forEach((e, row) => {
          let colors = "%c{#fff}%b{#000}"
          if (this._selectedItems[column] == -1) {
            this._selectedItems[column] = row
          }
          if (row == this._selectedItems[column]) {
            colors = "%c{#000}%b{#fff}"
          }
          printRow += 1
          let eStr = e.name
          if (e.twoHanded) {
            eStr += " (two-handed)"
          }

          const max = this._columnWidth - 2
          eStr = eStr.substring(0, max)

          display.drawText(xStart, printRow, colors + eStr)
        })
        xStart += this._columnWidth
        printRow = rowTop
      })
    }
  }

  update(action: PlayerAction): void {
    if (action == PlayerAction.MOVE_DOWN) {
      if (this._playerItems[this._currentColumn].length > 0) {
        this._currentRow += 1
        if (this._currentRow >= this._playerItems[this._currentColumn].length) {
          this._currentRow = 0
        }
        this._selectedItems[this._currentColumn] = this._currentRow
      }
    } else if (action == PlayerAction.MOVE_UP) {
      if (this._playerItems[this._currentColumn].length > 0) {
        this._currentRow -= 1
        if (this._currentRow < 0) {
          this._currentRow = this._playerItems[this._currentColumn].length - 1
        }
        this._selectedItems[this._currentColumn] = this._currentRow
      }
    } else if (action == PlayerAction.MOVE_LEFT) {
      this._currentColumn -= 1
      if (this._currentColumn < 0) {
        this._currentColumn = this._playerItems.length - 1
      }
      if (this._selectedItems[this._currentColumn] != -1) {
        this._currentRow = this._selectedItems[this._currentColumn]
      }
    } else if (action == PlayerAction.MOVE_RIGHT) {
      this._currentColumn += 1
      if (this._currentColumn >= this._playerItems.length) {
        this._currentColumn = 0
      }
      if (this._selectedItems[this._currentColumn] != -1) {
        this._currentRow = this._selectedItems[this._currentColumn]
      }
    }

    this.updateStats()
  }

  private updateStats(): void {
    const player = window.engine.getPlayer()
    player.equip(this.getSelectedEquipment())
    this._newStats = player.getEquipmentStats()
  }

  protected initTiles() {
    this._tiles = new Array(this.height)
    for (let y = 0; y < this.height; y++) {
      const isOddRow: boolean = y % 2 != 0 ? true : false
      const row = new Array(this.width)
      for (let x = 0; x < this.width; x++) {
        const isWall: boolean =
          x === 0 || x === this.width - 1 || y === 0 || y === this.height - 1
        row[x] = isWall ? { ...BORDER_TILE } : { ...BLACK_TILE }
      }
      this._tiles[y] = row
    }
  }
}
